import { Injectable } from '@angular/core';
import { HttpService } from 'app/services/http/http.service';
import { TenantService } from 'app/services/tenant/tenant.service';
import { BehaviorSubject, Observable, catchError, firstValueFrom, map, of, tap } from 'rxjs';

export type FeatureFlags = Record<string, boolean>;

interface FeatureFlagsResponse {
  flags: FeatureFlags;
}

@Injectable({
  providedIn: 'root'
})
export class FeatureFlagsService {
  private readonly _featureFlags$ = new BehaviorSubject<FeatureFlags>({});
  public featureFlags$ = this._featureFlags$.asObservable();
  private _loadingPromise: Promise<FeatureFlags> | null = null;
  private _loadingTenantId: string | null = null;
  /**
   * Tenant whose flags are currently on display. `undefined` until the first successful load.
   * Single source of truth for "is this tenant's cache good to reuse" — the loading bookkeeping
   * above tracks in-flight requests, not what actually got published, and a stale request that
   * resolves after the tenant has moved away must not make this look loaded either.
   */
  private _publishedTenantId: string | null | undefined = undefined;

  constructor(
    private readonly _httpService: HttpService,
    private readonly _tenantService: TenantService
  ) {
    // Root-scoped and never destroyed, so this subscription lives for the app's lifetime.
    this._tenantService.selectedTenant$.subscribe(() => this.reloadForNewTenant());
  }

  /**
   * A tenant switch invalidates every flag, so refetch eagerly rather than waiting for the next
   * guard to notice: the sidenav and any open screen read `featureFlags$` and would otherwise keep
   * showing the previous tenant's entitlements until the user happened to navigate.
   */
  private reloadForNewTenant(): void {
    void this.ensureFeatureFlagsLoaded();
  }

  /**
   * Ensures feature flags are loaded for the currently selected tenant. Safe to call multiple
   * times — it loads once per tenant. Flags are tenant-scoped, so a cache built for a different
   * tenant (or before one was chosen) is discarded rather than reused.
   * Call this after authentication is complete.
   */
  async ensureFeatureFlagsLoaded(): Promise<FeatureFlags> {
    const tenantId = this._tenantService.getSelectedTenant();

    // Already published for this tenant, return cached value
    if (this._publishedTenantId === tenantId) {
      return this._featureFlags$.value;
    }

    // Already loading for this tenant, return existing promise
    if (this._loadingPromise && this._loadingTenantId === tenantId) {
      return this._loadingPromise;
    }

    // Start loading
    this._loadingTenantId = tenantId;
    this._loadingPromise = firstValueFrom(this.loadFeatureFlags())
      .then(flags => {
        // A load for a newer tenant started while this one was in flight — let it win, don't
        // clear its loadingPromise out from under it
        if (this._loadingTenantId === tenantId) {
          this._loadingPromise = null;
        }
        return flags;
      })
      .catch(error => {
        this._loadingPromise = null;
        throw error;
      });

    return this._loadingPromise;
  }

  loadFeatureFlags(): Observable<FeatureFlags> {
    const requestedTenantId = this._tenantService.getSelectedTenant();
    const httpClient = this._httpService.createHttpClient();
    return httpClient
      .url('/fflag/api/v1/features')
      .get<FeatureFlagsResponse>()
      .pipe(
        map(response => response.flags),
        tap(flags => this.publishFlagsIfStillOnRequestedTenant(flags, requestedTenantId)),
        catchError(error => {
          console.error('Failed to load feature flags:', error);
          this.clearFlagsIfTheyBelongToAnotherTenant(requestedTenantId);
          // Return empty object on error to not block the app
          return of({});
        })
      );
  }

  /**
   * Switching away and back (A → B → A) leaves B's request in flight while A is served from cache.
   * Without this check B's late response would publish B's entitlements under tenant A.
   */
  private publishFlagsIfStillOnRequestedTenant(flags: FeatureFlags, requestedTenantId: string | null): void {
    if (this._tenantService.getSelectedTenant() !== requestedTenantId) {
      return;
    }

    this._publishedTenantId = requestedTenantId;
    this._featureFlags$.next(flags);
  }

  /**
   * A failed load leaves whatever loaded last on display. Retrying the same tenant may keep it — a
   * transient blip should not blank the menu — but flags belonging to a *different* tenant must be
   * dropped, or a failed reload after a tenant switch would leave the previous tenant's
   * entitlements visible. Fail closed instead: no flags until a load for this tenant succeeds.
   */
  private clearFlagsIfTheyBelongToAnotherTenant(requestedTenantId: string | null): void {
    const isShowingAnotherTenantsFlags = this._publishedTenantId !== undefined && this._publishedTenantId !== requestedTenantId;
    if (!isShowingAnotherTenantsFlags) {
      return;
    }

    this._publishedTenantId = requestedTenantId;
    this._featureFlags$.next({});
  }

  isFeatureEnabled(featureName: string): boolean {
    const flags = this._featureFlags$.value;
    // If feature flag doesn't exist, default to false (disabled)
    return flags[featureName] === true;
  }

  getFeatureFlags(): FeatureFlags {
    return this._featureFlags$.value;
  }
}
